import React, { useCallback, useMemo } from 'react';
import { FileError } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import {
  AppButton,
  Box,
  ButtonResponsiveWrapper,
  InvalidDocumentIcon,
  PasswordProtectedDocumentIcon,
  Text,
  useResponsive,
} from '@common-fe/common-fe';

import { MAX_SIZE_OF_FILE_IN_MEGABYTES } from '@/common/constants';
import { FileErrorCode } from '@/components/controls/DropScan/dropScan.types';
import { ModalWrapper } from '@/components/wrappers';

interface Props {
  fileError?: FileError;
  onClose: () => void;
  onUploadAnotherDocument?: () => void;
}

export const UploadFileErrorPopupContent: React.FC<Props> = ({ fileError, onClose, onUploadAnotherDocument }) => {
  const { isMobile } = useResponsive();
  const { t } = useTranslation();

  const errorIcon = useMemo(() => {
    switch (fileError?.code) {
      case FileErrorCode.PasswordProtectedDocument:
        return <PasswordProtectedDocumentIcon size="48px" />;
      case FileErrorCode.PasswordProtectedFile:
        return <PasswordProtectedDocumentIcon size="48px" />;
      default:
        return <InvalidDocumentIcon size="48px" />;
    }
  }, [fileError?.code]);

  const errorTitle = useMemo(() => {
    switch (fileError?.code) {
      case FileErrorCode.FileInvalidType:
        return t('Unsupported File Format');
      case FileErrorCode.InvalidFile:
        return t('Invalid File');
      case FileErrorCode.DocumentNameIsTooLong:
        return t(
          'The file name is too long. To upload it, please rename the file to be 200 characters or fewer.'
        );
      case FileErrorCode.PasswordProtectedDocument:
        return t('Uploaded document is password protected!');
      case FileErrorCode.PasswordProtectedFile:
        return t('Uploaded document is password protected!');
      default:
        return t('We can’t upload your document');
    }
  }, [t, fileError?.code]);

  const errorDescription = useMemo(() => {
    switch (fileError?.code) {
      case FileErrorCode.FileInvalidType:
        return (
          <>
            <Text>{t('Please upload file in such formats PDF, JPEG, JPG, PNG, TIF, HEIC, HEIF.')}</Text>
            <br />
            <Text>{t(`Max. file size: ${MAX_SIZE_OF_FILE_IN_MEGABYTES} MB`)}</Text>
          </>
        );
      case FileErrorCode.InvalidFile:
        return (
          <>
            <Text>{t('The uploaded file does not match the expected format.')}</Text>
            <br />
            <Text>{t('Please ensure the file is a valid PDF, JPEG, JPG, PNG, TIF, HEIC, or HEIF.')}</Text>
            <br />
            <Text>{t(`Max. file size: ${MAX_SIZE_OF_FILE_IN_MEGABYTES} MB`)}</Text>
          </>
        );
      case FileErrorCode.DocumentNameIsTooLong:
        return null;
      case FileErrorCode.PasswordProtectedDocument:
        return t(
          'We cannot upload this type of document. Please export or save it as a new unprotected version or take a picture of your document.'
        );
      case FileErrorCode.PasswordProtectedFile:
        return t(
          'We cannot upload this type of document. Please export or save it as a new unprotected version or take a picture of your document.'
        );
      default:
        return t(
          'The document you uploaded doesn’t meet our requirements. Please convert the file into an approved file type such as: PDF, HEIF, JPEG, JPG, PNG, TIFF and file size up to 20MB.'
        );
    }
  }, [t, fileError?.code]);

  const handleUploadAnotherDocument = useCallback(() => {
    if (onUploadAnotherDocument) {
      onUploadAnotherDocument();
      return;
    }
    onClose();
  }, [onUploadAnotherDocument, onClose]);

  return (
    <Box align="center" pad="spacing24">
      {errorIcon}
      <Text margin={{ vertical: 'spacing12' }} textAlign="center" size="2xl" weight={700}>
        {errorTitle}
      </Text>
      <Text margin={{ bottom: 'spacing24' }} textAlign="center" style={{ textWrap: 'balance' }}>
        {errorDescription}
      </Text>
      <ButtonResponsiveWrapper width="100%" justify="center">
        <AppButton fill={isMobile} onClick={onClose} buttonType="secondary" width="220px">
          {t('Close')}
        </AppButton>
        <AppButton fill={isMobile} onClick={handleUploadAnotherDocument} width="220px">
          {t('Upload Another Document')}
        </AppButton>
      </ButtonResponsiveWrapper>
    </Box>
  );
};

const UploadFileErrorPopup: React.FC<Props> = ({ fileError, onClose, onUploadAnotherDocument }) => (
  <ModalWrapper visible onSetVisible={onClose}>
    <UploadFileErrorPopupContent
      fileError={fileError}
      onClose={onClose}
      onUploadAnotherDocument={onUploadAnotherDocument}
    />
  </ModalWrapper>
);

export default UploadFileErrorPopup;
